import { useState } from 'react'
import {
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  PasswordInput,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'

// Only the CALLER's OWN organizations — never every organization in the system. No
// existing "Entity filter" org list lives on this page (BillingEntityControl's
// BILLING_ENTITY_OPTIONS is a fixed 3-value state enum, not organizations), so this
// derives membership the same way the server checks it (assertOrgMembership): the
// caller's own PractitionerRole rows, read fresh — `cache: 'no-cache'` on the request
// (MedplumClient otherwise caches GETs by URL) plus `staleTime: 0` below, since a stale
// "no roles yet" result is exactly the bug this replaced (see the report).
function useOwnOrganizationOptions() {
  const medplum = useMedplum()
  const profileId = medplum.getProfile()?.id

  return useQuery({
    queryKey: ['billing-settings-own-organizations', profileId],
    enabled: Boolean(profileId),
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => {
      const roles = await medplum.searchResources(
        'PractitionerRole',
        `practitioner=Practitioner/${profileId}`,
        { cache: 'no-cache' },
      )
      const orgIds = [
        ...new Set(
          roles
            .map((role) => role.organization?.reference?.split('/')[1])
            .filter((id): id is string => Boolean(id)),
        ),
      ]
      const orgs = await Promise.all(
        orgIds.map(async (id) => {
          try {
            const org = await medplum.readResource('Organization', id, { cache: 'no-cache' })
            return org.name ? { value: id, label: org.name } : null
          } catch {
            return null
          }
        }),
      )
      return orgs
        .filter((org): org is { value: string; label: string } => org !== null)
        .sort((a, b) => a.label.localeCompare(b.label))
    },
  })
}

function FieldRow({
  label,
  configured,
  placeholder,
  value,
  onChange,
}: {
  label: string
  configured: boolean
  placeholder: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div>
      <Group justify="space-between" mb={4}>
        <Text size="sm">{label}</Text>
        <Badge color={configured ? 'green' : 'gray'} variant="light">
          {configured ? 'Set' : 'Not set'}
        </Badge>
      </Group>
      <PasswordInput
        placeholder={placeholder}
        autoComplete="new-password"
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    </div>
  )
}

/**
 * Org-member-editable Billing settings (BillingV2 page header) — Stripe, Bridge and Stedi
 * credentials for whichever of the caller's OWN organizations is selected. Values are
 * write-only: the status endpoint returns booleans only, and this modal never displays a
 * stored value, only whatever the user is currently typing to replace it. Saved per
 * section (Stripe / Bridge) rather than per field or as one combined save, since each
 * section maps to one plugin's own credentials and the update endpoint already batches
 * whichever fields are given.
 */
export function BillingSettingsModal({ onClose }: { onClose: () => void }) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const { data: orgOptions, isLoading: orgsLoading } = useOwnOrganizationOptions()
  const [pickedOrganizationId, setPickedOrganizationId] = useState<string | null>(null)

  // Exactly one org membership: no picker at all, that org is simply the one in scope.
  const singleOrg = orgOptions?.length === 1 ? orgOptions[0] : null
  const organizationId = singleOrg ? singleOrg.value : pickedOrganizationId

  const { data: status, isLoading: statusLoading } = useQuery(
    orpc.billingSettingsStatus.queryOptions({
      input: { organizationId: organizationId ?? '' },
      enabled: Boolean(organizationId),
    }),
  )

  const update = useMutation(orpc.billingSettingsUpdate.mutationOptions())

  const [stripePublishableKey, setStripePublishableKey] = useState('')
  const [stripeSecretKey, setStripeSecretKey] = useState('')
  const [bridgeApiKey, setBridgeApiKey] = useState('')
  const [bridgeWebhookSecret, setBridgeWebhookSecret] = useState('')
  const [stediApiKey, setStediApiKey] = useState('')

  const invalidateStatus = () => {
    void queryClient.invalidateQueries({ queryKey: orpc.billingSettingsStatus.key() })
  }

  const handleSaveStripe = async () => {
    if (!organizationId) return
    // Stripe requires the pair together — the shared service (applyOrganizationStripeKeys)
    // validates the secret key live and auto-registers the co-pay webhook itself; there is
    // no partial/webhook-secret field here to fill in by hand.
    if (!stripePublishableKey.trim() || !stripeSecretKey.trim()) return
    try {
      await update.mutateAsync({
        organizationId,
        stripe: { publishableKey: stripePublishableKey.trim(), secretKey: stripeSecretKey.trim() },
      })
      setStripePublishableKey('')
      setStripeSecretKey('')
      invalidateStatus()
      showNotification({ color: 'green', message: 'Stripe settings saved' })
    } catch (error) {
      showNotification({
        color: 'red',
        message: error instanceof Error ? error.message : 'Failed to save Stripe settings',
      })
    }
  }

  const handleSaveStedi = async () => {
    if (!organizationId) return
    if (!stediApiKey.trim()) return
    try {
      await update.mutateAsync({ organizationId, stedi: { apiKey: stediApiKey.trim() } })
      setStediApiKey('')
      invalidateStatus()
      showNotification({ color: 'green', message: 'Stedi settings saved' })
    } catch (error) {
      showNotification({
        color: 'red',
        message: error instanceof Error ? error.message : 'Failed to save Stedi settings',
      })
    }
  }

  const handleSaveBridge = async () => {
    if (!organizationId) return
    const bridge: { apiKey?: string; webhookSecret?: string } = {}
    if (bridgeApiKey.trim()) bridge.apiKey = bridgeApiKey.trim()
    if (bridgeWebhookSecret.trim()) bridge.webhookSecret = bridgeWebhookSecret.trim()
    if (Object.keys(bridge).length === 0) return
    try {
      await update.mutateAsync({ organizationId, bridge })
      setBridgeApiKey('')
      setBridgeWebhookSecret('')
      invalidateStatus()
      showNotification({ color: 'green', message: 'Bridge settings saved' })
    } catch (error) {
      showNotification({
        color: 'red',
        message: error instanceof Error ? error.message : 'Failed to save Bridge settings',
      })
    }
  }

  return (
    <Modal opened onClose={onClose} title="Billing settings" size="md">
      <Stack gap="md">
        {orgsLoading ? (
          <Loader size="sm" />
        ) : !orgOptions || orgOptions.length === 0 ? (
          <Text size="sm" c="dimmed">
            You are not a member of any organization, so there is nothing to configure here.
          </Text>
        ) : singleOrg ? (
          <Text size="sm" fw={500}>
            {singleOrg.label}
          </Text>
        ) : (
          <Select
            label="Organization"
            placeholder="Select an organization"
            data={orgOptions}
            value={pickedOrganizationId}
            onChange={setPickedOrganizationId}
            searchable
          />
        )}

        {organizationId ? (
          statusLoading ? (
            <Loader size="sm" />
          ) : (
            <>
              <Stack gap="xs">
                <Title order={5}>Stripe</Title>
                <FieldRow
                  label="Publishable key"
                  configured={status?.stripe.publishableKeyConfigured ?? false}
                  placeholder="pk_…"
                  value={stripePublishableKey}
                  onChange={setStripePublishableKey}
                />
                <FieldRow
                  label="Secret key"
                  configured={status?.stripe.secretKeyConfigured ?? false}
                  placeholder="sk_…"
                  value={stripeSecretKey}
                  onChange={setStripeSecretKey}
                />
                <Text size="xs" c="dimmed">
                  Keys are validated against Stripe and stored server-side on save. Saving also
                  registers the co-pay webhook in the organization&apos;s Stripe account.
                </Text>
                <Group justify="flex-end">
                  <Button
                    size="xs"
                    onClick={() => void handleSaveStripe()}
                    loading={update.isPending}
                  >
                    Save Stripe
                  </Button>
                </Group>
              </Stack>

              <Stack gap="xs">
                <Title order={5}>Bridge</Title>
                <FieldRow
                  label="API key"
                  configured={status?.bridge.apiKeyConfigured ?? false}
                  placeholder="sk_…"
                  value={bridgeApiKey}
                  onChange={setBridgeApiKey}
                />
                <FieldRow
                  label="Webhook secret"
                  configured={status?.bridge.webhookSecretConfigured ?? false}
                  placeholder="whsec_…"
                  value={bridgeWebhookSecret}
                  onChange={setBridgeWebhookSecret}
                />
                <Group justify="flex-end">
                  <Button
                    size="xs"
                    onClick={() => void handleSaveBridge()}
                    loading={update.isPending}
                  >
                    Save Bridge
                  </Button>
                </Group>
              </Stack>

              <Stack gap="xs">
                <Title order={5}>Stedi</Title>
                <FieldRow
                  label="API key"
                  configured={status?.stedi.apiKeyConfigured ?? false}
                  placeholder="test_… or a live key"
                  value={stediApiKey}
                  onChange={setStediApiKey}
                />
                <Text size="xs" c="dimmed">
                  Test keys (<code>test_…</code>) keep every Stedi transaction mock; a live key
                  sends real traffic to payers.
                </Text>
                <Group justify="flex-end">
                  <Button
                    size="xs"
                    onClick={() => void handleSaveStedi()}
                    loading={update.isPending}
                  >
                    Save Stedi
                  </Button>
                </Group>
              </Stack>
            </>
          )
        ) : null}
      </Stack>
    </Modal>
  )
}
